// TODO STUDIO MIGRATION (Phase 5 prep): middleware now returns { jobId, room } for TTS avatar dispatches. Migrate from setInterval polling to useJobSocket(jobId, onEvent) from _editor/utils/jobSocket.js. See aidea-middleware spec docs/superpowers/specs/2026-05-25-producer-worker-discipline-design.md §7.
import React from "react";
import PropTypes from "prop-types";
import i18n from "i18next";

import placeholder from "../logos/soundcloud_placeholder.png";
import { ID_PREFIX_FILE } from "../../../../../../common/constants.es6";
import { getToken } from "../../../../../../common/environment_config.es6";
import { approveJob } from "../../../../../../common/server_utils.es6";
import { isFileTooLarge, fileTooLargeMessage } from "../../../../../../common/upload_limits.es6";
import { getMediaDuration } from "../../../../../utils/mediaDuration";
import { credentialsFor } from "../../../../../../common/utils";
import "./_avatarTextToSpeech.scss";
import { pickContentLocale } from "../../../../../../common/content_language";

const PROVIDERS = [
  { value: "azure-video", label: "Azure Avatar" },
  { value: "azure-photo", label: "Azure Photo Avatar" },
  { value: "tavus", label: "Tavus Avatar" },
  { value: "aidea", label: "Aidea (Stock/Video/Image + Audio/TTS)" },
];

const GENDER_FILTERS = [
  {
    value: "all",
    labelKey: "FileModal.APIProviders.AIAvatars.AllGenders",
    defaultLabel: "All Genders",
  },
  { value: "male", labelKey: "FileModal.APIProviders.AIAvatars.Male", defaultLabel: "Male" },
  { value: "female", labelKey: "FileModal.APIProviders.AIAvatars.Female", defaultLabel: "Female" },
];

const STEPS = [
  {
    id: 1,
    labelKey: "FileModal.APIProviders.AIAvatars.Step1SelectAvatar",
    defaultLabel: "Select Avatar",
  },
  {
    id: 2,
    labelKey: "FileModal.APIProviders.AIAvatars.Step2Settings",
    defaultLabel: "Settings & Create",
  },
];

const AIDEA_SOURCE_TYPES = [
  {
    value: "stock-avatar",
    label: "Stock Avatar",
    labelKey: "FileModal.APIProviders.AIAvatars.StockAvatar",
    defaultLabel: "Stock Avatar",
  },
  {
    value: "upload-video",
    label: "Clone from Video",
    labelKey: "FileModal.APIProviders.AIAvatars.UploadVideo",
    defaultLabel: "Clone from Video",
  },
  {
    value: "image-to-video",
    label: "Clone from Image",
    labelKey: "FileModal.APIProviders.AIAvatars.ImageToVideo",
    defaultLabel: "Clone from Image",
  },
];

const IMAGE_TO_VIDEO_PROVIDERS = [
  { value: "gemini", label: "Gemini" },
  { value: "grok", label: "Grok" },
];

const AUDIO_SOURCE_TYPES = [
  {
    value: "upload-audio",
    label: "Upload Audio",
    labelKey: "FileModal.APIProviders.AIAvatars.UploadAudio",
    defaultLabel: "Upload Audio",
  },
  {
    value: "text-to-speech",
    label: "Text to Speech",
    labelKey: "FileModal.APIProviders.AIAvatars.TextToSpeech",
    defaultLabel: "Text to Speech",
  },
];

const TTS_PROVIDERS = [
  { value: "azure", label: "Azure (Microsoft)" },
  { value: "elevenlabs", label: "ElevenLabs" },
  { value: "gemini", label: "Google Gemini" },
];

export default class AvatarTextToSpeechComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 1,
      results: [],
      query: "",
      msg: i18n.t("FileModal.APIProviders.no_files"),
      processing: false,
      videoUrl: "",
      avatarLoaded: false,
      avatars: [],
      // Aidea specific state
      aideaSourceType: null, // stock-avatar, upload-video, image-to-video
      aideaAvatars: [],
      selectedAideaAvatar: null,
      uploadedVideo: null,
      uploadedVideoUrl: null,
      imageToVideoProvider: null, // gemini or grok
      uploadedImage: null,
      uploadedImageUrl: null,
      audioSourceType: null, // upload-audio or text-to-speech
      uploadedAudio: null,
      uploadedAudioUrl: null,
      filteredAvatars: [],
      selectedAvatar: null,
      selectedProvider: "azure-video",
      genderFilter: "all",
      jobId: null,
      jobStatus: null,
      polling: false,
      editMode: false,
      editingVideo: null,
      // Voice related state
      allVoices: [],
      voices: [],
      selectedVoice: "",
      selectedLanguage: pickContentLocale(["tr-TR", "en-US", "az-AZ"], "tr-TR"),
      isLoadingVoices: false,
      // Aidea specific state
      aideaSourceType: null, // stock-avatar, upload-video, image-to-video
      aideaAvatars: [],
      selectedAideaAvatar: null,
      uploadedVideo: null,
      uploadedVideoUrl: null,
      imageToVideoProvider: null, // gemini or grok
      uploadedImage: null,
      uploadedImageUrl: null,
      audioSourceType: null, // upload-audio or text-to-speech
      uploadedAudio: null,
      uploadedAudioUrl: null,
      uploadedAudioFileUrl: null, // Backend file URL
      uploadingAudio: false,
      uploadedVideoFileUrl: null, // Backend file URL
      uploadingVideo: false,
      uploadedImageFileUrl: null, // Backend file URL (for image-to-video)
      uploadingImage: false,
      // TTS specific state
      ttsProvider: null, // azure, elevenlabs, gemini
      ttsVoices: [],
      ttsSelectedLanguage: "",
      ttsSelectedVoice: null,
      ttsText: "",
      isLoadingTTSVoices: false,
      transparentBackground: false,
      aideaGenderFilter: "all", // for upload-video/image-to-video
    };

    this.avatarGenerateHandler = this.avatarGenerateHandler.bind(this);
    this.selectAvatar = this.selectAvatar.bind(this);
    this.checkJobStatus = this.checkJobStatus.bind(this);
    this.startJobPolling = this.startJobPolling.bind(this);
    this.stopJobPolling = this.stopJobPolling.bind(this);
    this.toggleEditMode = this.toggleEditMode.bind(this);
    this.loadExistingVideos = this.loadExistingVideos.bind(this);
    this.handleProviderChange = this.handleProviderChange.bind(this);
    this.handleGenderFilterChange = this.handleGenderFilterChange.bind(this);
    this.applyGenderFilter = this.applyGenderFilter.bind(this);
    this.loadVoices = this.loadVoices.bind(this);
    this.filterVoicesByGender = this.filterVoicesByGender.bind(this);
    this.goToStep = this.goToStep.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handlePrevStep = this.handlePrevStep.bind(this);
    this.loadAideaAvatars = this.loadAideaAvatars.bind(this);
    this.handleAideaSourceTypeChange = this.handleAideaSourceTypeChange.bind(this);
    this.loadTTSVoices = this.loadTTSVoices.bind(this);
    this.getTTSLanguages = this.getTTSLanguages.bind(this);
    this.getFilteredTTSVoices = this.getFilteredTTSVoices.bind(this);
    this.handleVideoUpload = this.handleVideoUpload.bind(this);
    this.handleImageUpload = this.handleImageUpload.bind(this);
    this.handleAudioUpload = this.handleAudioUpload.bind(this);
    this.handleAudioSourceTypeChange = this.handleAudioSourceTypeChange.bind(this);
  }

  getGenderLabel = (gender) => {
    if (!gender) return "";
    const key = gender.toLowerCase();
    const map = {
      male: i18n.t("FileModal.APIProviders.AIAvatars.Male") || "Male",
      female: i18n.t("FileModal.APIProviders.AIAvatars.Female") || "Female",
    };
    return map[key] || gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  getProviderLabel = (providerType) => {
    if (!providerType) {
      return i18n.t("FileModal.APIProviders.AIAvatars.ProviderTypes.AI") || "AI";
    }
    const key = providerType.toLowerCase();
    const map = {
      ai: i18n.t("FileModal.APIProviders.AIAvatars.ProviderTypes.AI") || "AI",
      human: i18n.t("FileModal.APIProviders.AIAvatars.ProviderTypes.Human") || "Human",
    };
    return map[key] || providerType;
  };

  componentWillUnmount() {
    this.stopJobPolling();

    // Revoke object URLs to prevent memory leaks
    if (this.state.uploadedVideoUrl) {
      URL.revokeObjectURL(this.state.uploadedVideoUrl);
    }
    if (this.state.uploadedImageUrl) {
      URL.revokeObjectURL(this.state.uploadedImageUrl);
    }
    if (this.state.uploadedAudioUrl) {
      URL.revokeObjectURL(this.state.uploadedAudioUrl);
    }

    const videoElement = document.getElementById("avatar_video");
    if (videoElement) {
      videoElement.pause();
    }
  }

  async componentDidMount() {
    // Her açılışta uploaded file state'lerini temizle
    this.setState({
      uploadedVideo: null,
      uploadedVideoUrl: null,
      uploadedVideoFileUrl: null,
      uploadedImage: null,
      uploadedImageUrl: null,
      uploadedImageFileUrl: null,
      uploadedAudio: null,
      uploadedAudioUrl: null,
      uploadedAudioFileUrl: null,
      uploadingVideo: false,
      uploadingImage: false,
      uploadingAudio: false,
    });

    await this.loadAvatars();
    if (
      this.state.selectedProvider === "azure-video" ||
      this.state.selectedProvider === "azure-photo"
    ) {
      await this.loadVoices();
    }

    // Eğer AI Sounds tarafında bir anlatım metni yazıldıysa, onu varsayılan senaryo metni olarak al
    try {
      const avatarPrompt = document.getElementById("avatar_prompttext");
      if (avatarPrompt && !avatarPrompt.value && window.lastAiNarrationPrompt) {
        avatarPrompt.value = window.lastAiNarrationPrompt;
      }
    } catch (e) {
      // Global veya DOM erişiminde sorun olursa sessizce devam et
    }
  }

  goToStep(step) {
    const {
      selectedProvider,
      aideaSourceType,
      selectedAideaAvatar,
      uploadedVideo,
      uploadedImage,
      imageToVideoProvider,
    } = this.state;

    if (step === 2) {
      if (selectedProvider === "aidea") {
        // Check if all required Aidea selections are made
        if (!aideaSourceType) return;
        if (aideaSourceType === "stock-avatar" && !selectedAideaAvatar) return;
        if (aideaSourceType === "upload-video" && !uploadedVideo) return;
        if (aideaSourceType === "image-to-video" && (!imageToVideoProvider || !uploadedImage))
          return;
      } else if (!this.state.selectedAvatar) {
        return;
      }
    }
    this.setState({ currentStep: step });
  }

  handleNextStep() {
    const {
      currentStep,
      selectedProvider,
      aideaSourceType,
      selectedAideaAvatar,
      uploadedVideo,
      uploadedImage,
      imageToVideoProvider,
    } = this.state;

    if (currentStep === 1) {
      if (selectedProvider === "aidea") {
        // Validate Aidea selections
        if (!aideaSourceType) return;
        if (aideaSourceType === "stock-avatar" && !selectedAideaAvatar) return;
        if (aideaSourceType === "upload-video" && !uploadedVideo) return;
        if (aideaSourceType === "image-to-video" && (!imageToVideoProvider || !uploadedImage))
          return;
      } else if (!this.state.selectedAvatar) {
        return;
      }

      this.setState({ currentStep: 2 });
    }
  }

  handlePrevStep() {
    if (this.state.currentStep === 2) {
      // Stop any ongoing polling
      this.stopJobPolling();

      // Reset all processing states
      this.setState({
        currentStep: 1,
        processing: false,
        polling: false,
        jobId: null,
        jobStatus: null,
        videoUrl: "",
        avatarLoaded: false,
      });
    }
  }

  async loadVoices() {
    this.setState({ isLoadingVoices: true });

    try {
      const response = await fetch(
        `${process.env.MW_API_URL}api/course/available-voices?service=microsoft`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const voiceList = Array.isArray(data?.voices) ? data.voices : [];
        this.setState({ allVoices: voiceList }, () => {
          this.filterVoicesByGender();
        });
      } else {
        throw new Error("Failed to fetch voices");
      }
    } catch (error) {
      console.error("Error loading voices:", error);
      this.setState({ allVoices: [], voices: [] });
    } finally {
      this.setState({ isLoadingVoices: false });
    }
  }

  filterVoicesByGender() {
    const { selectedAvatar, allVoices, selectedProvider } = this.state;

    if (
      (selectedProvider === "azure-video" || selectedProvider === "azure-photo") &&
      selectedAvatar &&
      allVoices.length > 0
    ) {
      const filteredVoices = allVoices.filter((voice) => voice.gender === selectedAvatar.gender);
      this.setState({
        voices: filteredVoices,
        selectedVoice: filteredVoices.length > 0 ? filteredVoices[0].id : "",
      });
    } else {
      this.setState({ voices: [], selectedVoice: "" });
    }
  }

  async loadAvatars(providerType = null) {
    const provider = providerType || this.state.selectedProvider;

    try {
      this.setState({
        msg: i18n.t("FileModal.APIProviders.AIAvatars.LoadingAvatars"),
        avatars: [],
        filteredAvatars: [],
      });

      const response = await fetch(`${process.env.MW_API_URL}api/tts-avatar/provider/${provider}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const avatarsData = Array.isArray(data) ? data.filter((avatar) => avatar.isActive) : [];

        this.setState(
          {
            avatars: avatarsData,
            msg:
              avatarsData.length > 0
                ? ""
                : i18n.t("FileModal.APIProviders.AIAvatars.NoAvatarsAvailable"),
          },
          () => {
            this.applyGenderFilter();
          }
        );
      } else {
        throw new Error("Failed to fetch avatars");
      }
    } catch (error) {
      console.error("Error loading avatars:", error);
      this.setState({
        avatars: [],
        filteredAvatars: [],
        msg: i18n.t("FileModal.APIProviders.AIAvatars.ErrorLoadingAvatars"),
      });
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.FailedToLoadAvatars") + "</span>",
        false
      );
    }
  }

  async loadAideaAvatars() {
    try {
      this.setState({
        msg: i18n.t("FileModal.APIProviders.AIAvatars.LoadingAvatars"),
        aideaAvatars: [],
      });

      const backendUrl = `${process.env.MW_API_URL}api/tts-avatar/provider/aidea`;

      const response = await fetch(backendUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const avatarsData = Array.isArray(data) ? data.filter((avatar) => avatar.isActive) : [];

        this.setState({
          aideaAvatars: avatarsData,
          msg:
            avatarsData.length > 0
              ? ""
              : i18n.t("FileModal.APIProviders.AIAvatars.NoAvatarsAvailable"),
        });
      } else {
        throw new Error("Failed to fetch Aidea avatars");
      }
    } catch (error) {
      console.error("Error loading Aidea avatars:", error);
      this.setState({
        aideaAvatars: [],
        msg: i18n.t("FileModal.APIProviders.AIAvatars.ErrorLoadingAvatars"),
      });
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.FailedToLoadAvatars") + "</span>",
        false
      );
    }
  }

  async loadTTSVoices(provider) {
    if (!provider) return;

    try {
      this.setState({
        isLoadingTTSVoices: true,
        ttsVoices: [],
        ttsSelectedLanguage: "",
        ttsSelectedVoice: null,
      });

      // Map provider names to API service names
      const serviceMap = {
        azure: "microsoft",
        elevenlabs: "elevenlabs",
        gemini: "gemini",
      };

      const serviceName = serviceMap[provider] || provider;
      const backendUrl = `${process.env.MW_API_URL}api/course/available-voices?service=${serviceName}`;

      const response = await fetch(backendUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const voices = data.voices || [];

        this.setState({
          ttsVoices: voices,
          isLoadingTTSVoices: false,
        });
      } else {
        throw new Error("Failed to fetch TTS voices");
      }
    } catch (error) {
      console.error("Error loading TTS voices:", error);
      this.setState({
        ttsVoices: [],
        isLoadingTTSVoices: false,
      });
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>Failed to load voices. Please try again.</span>",
        false
      );
    }
  }

  getTTSLanguages() {
    const { ttsVoices } = this.state;
    if (!ttsVoices.length) return [];

    // Get unique languages from voices
    const languages = [...new Set(ttsVoices.map((v) => v.language))];
    return languages.filter(Boolean).sort();
  }

  getFilteredTTSVoices() {
    const { ttsVoices, aideaSourceType, selectedAideaAvatar, aideaGenderFilter } = this.state;

    let filtered = ttsVoices;

    // Filter by gender based on source type
    let targetGender = null;
    if (aideaSourceType === "stock-avatar" && selectedAideaAvatar) {
      // Use avatar's gender for stock avatars
      targetGender = selectedAideaAvatar.gender;
    } else if (aideaSourceType === "upload-video" || aideaSourceType === "image-to-video") {
      // Use manual gender filter for uploaded content
      if (aideaGenderFilter !== "all") {
        targetGender = aideaGenderFilter;
      }
    }

    if (targetGender) {
      filtered = filtered.filter((voice) => voice.gender === targetGender);
    }

    return filtered;
  }

  handleAideaSourceTypeChange(sourceType) {
    // Stop any ongoing polling when changing source type
    this.stopJobPolling();

    this.setState({
      aideaSourceType: sourceType,
      selectedAideaAvatar: null,
      uploadedVideo: null,
      uploadedVideoUrl: null,
      uploadedVideoFileUrl: null,
      imageToVideoProvider: null,
      uploadedImage: null,
      uploadedImageUrl: null,
      uploadedImageFileUrl: null,
      aideaGenderFilter: "all",
      ttsSelectedVoice: null,
      processing: false,
      polling: false,
      jobId: null,
      jobStatus: null,
      videoUrl: "",
      avatarLoaded: false,
    });
  }

  handleAudioSourceTypeChange(sourceType) {
    // Revoke old audio URL if exists to prevent memory leak
    if (this.state.uploadedAudioUrl) {
      URL.revokeObjectURL(this.state.uploadedAudioUrl);
    }

    this.setState({
      audioSourceType: sourceType,
      uploadedAudio: null,
      uploadedAudioUrl: null,
      uploadedAudioFileUrl: null,
      uploadingAudio: false,
      ttsText: "",
      ttsSelectedVoice: null,
    });
  }

  async handleVideoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // 500 MB üst sınırı: aşan dosyayı kabul etme
    if (isFileTooLarge(file)) {
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>" + fileTooLargeMessage() + "</span>",
        false
      );
      e.target.value = ""; // Input'u sıfırla
      return;
    }

    // Create local preview URL
    const videoUrl = URL.createObjectURL(file);
    this.setState({
      uploadedVideo: file,
      uploadedVideoUrl: videoUrl,
      uploadingVideo: true,
      uploadedVideoFileUrl: null,
    });

    try {
      const searchParams = new URLSearchParams(window.location.search);
      const trainingId = searchParams?.get("id");

      const formData = new FormData();
      formData.append("file", file);

      const queryParams = new URLSearchParams();
      if (trainingId) {
        queryParams.set("trainingId", trainingId);
      }
      queryParams.set("fileDescription", "Aidea Avatar Video");

      const response = await fetch(
        `${process.env.MW_API_URL}api/tts-avatar/upload-media?${queryParams.toString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.setState({
          uploadedVideoFileUrl: data.fileUrl,
          uploadingVideo: false,
        });
      } else {
        throw new Error("Failed to upload video");
      }
    } catch (error) {
      console.error("Error uploading video:", error);
      this.setState({
        uploadingVideo: false,
        uploadedVideoFileUrl: null,
      });
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>Failed to upload video. Please try again.</span>",
        false
      );
    }
  }

  async handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // 500 MB üst sınırı: aşan dosyayı kabul etme
    if (isFileTooLarge(file)) {
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>" + fileTooLargeMessage() + "</span>",
        false
      );
      e.target.value = ""; // Input'u sıfırla
      return;
    }

    // Create local preview URL only (no API upload for image-to-video)
    const imageUrl = URL.createObjectURL(file);
    this.setState({
      uploadedImage: file,
      uploadedImageUrl: imageUrl,
      uploadingImage: false,
    });
  }

  async handleAudioUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // 500 MB üst sınırı: aşan dosyayı kabul etme
    if (isFileTooLarge(file)) {
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>" + fileTooLargeMessage() + "</span>",
        false
      );
      e.target.value = ""; // Input'u sıfırla
      return;
    }

    // Create local preview URL
    const audioUrl = URL.createObjectURL(file);
    this.setState({
      uploadedAudio: file,
      uploadedAudioUrl: audioUrl,
      uploadingAudio: true,
      uploadedAudioFileUrl: null,
    });

    try {
      const searchParams = new URLSearchParams(window.location.search);
      const trainingId = searchParams?.get("id");

      const formData = new FormData();
      formData.append("file", file);

      const queryParams = new URLSearchParams();
      if (trainingId) {
        queryParams.set("trainingId", trainingId);
      }
      queryParams.set("fileDescription", "Aidea Avatar Audio");

      const response = await fetch(
        `${process.env.MW_API_URL}api/tts-avatar/upload-media?${queryParams.toString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.setState({
          uploadedAudioFileUrl: data.fileUrl,
          uploadingAudio: false,
        });
      } else {
        throw new Error("Failed to upload audio");
      }
    } catch (error) {
      console.error("Error uploading audio:", error);
      this.setState({
        uploadingAudio: false,
        uploadedAudioFileUrl: null,
      });
      this.props.onServerModal?.(
        i18n.t("error.warning"),
        "<span>Failed to upload audio. Please try again.</span>",
        false
      );
    }
  }

  handleProviderChange(e) {
    const provider = e.target.value;

    // Stop any ongoing polling when changing provider
    this.stopJobPolling();

    this.setState(
      {
        selectedProvider: provider,
        selectedAvatar: null,
        videoUrl: "",
        avatarLoaded: false,
        voices: [],
        selectedVoice: "",
        currentStep: 1,
        processing: false,
        polling: false,
        jobId: null,
        jobStatus: null,
        // Reset Aidea state
        aideaSourceType: null,
        selectedAideaAvatar: null,
        uploadedVideo: null,
        uploadedVideoUrl: null,
        imageToVideoProvider: null,
        uploadedImage: null,
        uploadedImageUrl: null,
        audioSourceType: null,
        uploadedAudio: null,
        uploadedAudioUrl: null,
        uploadedAudioFileUrl: null,
        aideaGenderFilter: "all",
        ttsProvider: null,
        ttsVoices: [],
        ttsSelectedLanguage: "",
        ttsSelectedVoice: null,
        ttsText: "",
      },
      async () => {
        if (provider === "aidea") {
          await this.loadAideaAvatars();
        } else {
          await this.loadAvatars(provider);
          if (provider === "azure-video" || provider === "azure-photo") {
            await this.loadVoices();
          }
        }
      }
    );
  }

  handleGenderFilterChange(e) {
    const gender = e.target.value;
    this.setState({ genderFilter: gender }, () => {
      this.applyGenderFilter();
    });
  }

  applyGenderFilter() {
    const { avatars, genderFilter } = this.state;

    if (genderFilter === "all") {
      this.setState({ filteredAvatars: avatars });
    } else {
      const filtered = avatars.filter((avatar) => avatar.gender === genderFilter);
      this.setState({ filteredAvatars: filtered });
    }
  }

  selectAvatar(avatar) {
    // Stop any ongoing polling when selecting a new avatar
    this.stopJobPolling();

    this.setState(
      {
        selectedAvatar: avatar,
        processing: false,
        polling: false,
        jobId: null,
        jobStatus: null,
        videoUrl: "",
        avatarLoaded: false,
      },
      () => {
        this.filterVoicesByGender();
      }
    );
  }

  toggleEditMode() {
    this.setState({
      editMode: !this.state.editMode,
      editingVideo: null,
    });
  }

  async loadExistingVideos() {
    return [];
  }

  renderStepIndicator() {
    const { currentStep, selectedAvatar } = this.state;

    return (
      <div className="stepper-header">
        {STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            <div
              className={`step-item ${currentStep === step.id ? "active" : ""} ${currentStep > step.id ? "completed" : ""} ${step.id === 2 && !selectedAvatar ? "disabled" : ""}`}
              onClick={() => this.goToStep(step.id)}
            >
              <div className="step-number">
                {currentStep > step.id ? <i className="material-icons">check</i> : step.id}
              </div>
              <div className="step-label">{i18n.t(step.labelKey) || step.defaultLabel}</div>
            </div>
            {index < STEPS.length - 1 && <div className="step-connector" />}
          </React.Fragment>
        ))}
      </div>
    );
  }

  renderStep1() {
    const { selectedProvider } = this.state;

    // Aidea flow - similar to Azure/Tavus but with source type selection
    if (selectedProvider === "aidea") {
      return (
        <div className="step-content step-1">
          <div className="avatar-selection">
            <div className="filter-section">
              <div className="form-row inline">
                <div className="form-group">
                  <h5 id="field-label">
                    {i18n.t("FileModal.APIProviders.AIAvatars.ProviderType") || "Provider Type"}
                  </h5>
                  <select
                    value={this.state.selectedProvider}
                    onChange={this.handleProviderChange}
                    className="select"
                  >
                    {PROVIDERS.map((provider) => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <h5 id="field-label">
                    {i18n.t("FileModal.APIProviders.AIAvatars.SourceType") || "Source Type"}
                  </h5>
                  <select
                    value={this.state.aideaSourceType || ""}
                    onChange={(e) => this.handleAideaSourceTypeChange(e.target.value)}
                    className="select"
                  >
                    <option value="">
                      {i18n.t("FileModal.APIProviders.AIAvatars.SelectSourceType") ||
                        "Select Source Type"}
                    </option>
                    {AIDEA_SOURCE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {i18n.t(type.labelKey) || type.defaultLabel}
                      </option>
                    ))}
                  </select>
                </div>

                {this.state.aideaSourceType === "image-to-video" && (
                  <div className="form-group">
                    <h5 id="field-label">
                      {i18n.t("FileModal.APIProviders.AIAvatars.ImageProvider") || "Image Provider"}
                    </h5>
                    <select
                      value={this.state.imageToVideoProvider || ""}
                      onChange={(e) => this.setState({ imageToVideoProvider: e.target.value })}
                      className="select"
                    >
                      <option value="">
                        {i18n.t("FileModal.APIProviders.AIAvatars.SelectProvider") ||
                          "Select Provider"}
                      </option>
                      {IMAGE_TO_VIDEO_PROVIDERS.map((provider) => (
                        <option key={provider.value} value={provider.value}>
                          {provider.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Stock Avatar Grid */}
            {this.state.aideaSourceType === "stock-avatar" && (
              <div className="avatar-grid-container">
                <div className="avatar-grid">
                  {this.state.aideaAvatars.length === 0 && this.state.msg ? (
                    <div className="loading-state">
                      <i className="material-icons icon">
                        {this.state.msg.includes("Loading") ? "hourglass_empty" : "error_outline"}
                      </i>
                      <div className="message">{this.state.msg}</div>
                    </div>
                  ) : this.state.aideaAvatars.length === 0 ? (
                    <div className="loading-state">
                      <i className="material-icons icon">person_off</i>
                      <div className="message">
                        {i18n.t("FileModal.APIProviders.AIAvatars.NoAvatarsAvailable") ||
                          "No avatars available"}
                      </div>
                    </div>
                  ) : (
                    this.state.aideaAvatars.map((avatar) => (
                      <div
                        key={avatar.id}
                        className={`avatar-card ${this.state.selectedAideaAvatar?.id === avatar.id ? "selected" : ""}`}
                        onClick={() =>
                          this.setState({
                            selectedAideaAvatar: avatar,
                            ttsSelectedVoice: null, // Reset voice selection when avatar changes
                          })
                        }
                      >
                        <div className="card-body">
                          <div className="image-container">
                            {avatar.thumbnailUrl ? (
                              <img
                                src={avatar.thumbnailUrl}
                                alt={avatar.name}
                                className="thumbnail"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className={`placeholder-icon ${avatar.thumbnailUrl ? "" : "visible"}`}
                              style={{ display: avatar.thumbnailUrl ? "none" : "flex" }}
                            >
                              <i className="material-icons icon">smart_toy</i>
                            </div>
                            {avatar.videoUrl && (
                              <div className="duration-badge">
                                {avatar.duration ? `${avatar.duration}s` : "Preview"}
                              </div>
                            )}
                            {avatar.category && (
                              <div className="category-badge">{avatar.category}</div>
                            )}
                          </div>
                          <div className="info">
                            <div className="name" title={avatar.name}>
                              {avatar.name}
                            </div>
                            {avatar.gender && (
                              <div
                                className="details"
                                title={`${avatar.gender.charAt(0).toUpperCase()}${avatar.gender.slice(1)} • ${avatar.providerType || "AI"}`}
                              >
                                {avatar.gender.charAt(0).toUpperCase() + avatar.gender.slice(1)} •{" "}
                                {avatar.providerType || "AI"}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Upload Video */}
            {this.state.aideaSourceType === "upload-video" && (
              <div className="avatar-grid-container">
                <div className="upload-area">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={this.handleVideoUpload}
                    style={{ display: "none" }}
                    id="video-upload-input"
                  />
                  {this.state.uploadingVideo ? (
                    <div className="upload-box uploading">
                      <i className="material-icons upload-icon rotating">hourglass_empty</i>
                      <div className="upload-text">
                        {i18n.t("FileModal.APIProviders.AIAvatars.UploadingVideo") ||
                          "Uploading video..."}
                      </div>
                    </div>
                  ) : this.state.uploadedVideoUrl ? (
                    <div className="preview-wrapper">
                      <video
                        src={this.state.uploadedVideoUrl}
                        controls
                        className="uploaded-preview"
                      />
                      <button
                        className="change-file-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("video-upload-input").click();
                        }}
                      >
                        <i className="material-icons">refresh</i>
                        {i18n.t("FileModal.APIProviders.AIAvatars.ChangeVideo") || "Change Video"}
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="video-upload-input" className="upload-box">
                      <i className="material-icons upload-icon">videocam</i>
                      <div className="upload-text">
                        {i18n.t("FileModal.APIProviders.AIAvatars.ClickToUploadVideo") ||
                          "Click to upload video"}
                      </div>
                      <div className="upload-hint">
                        {i18n.t("FileModal.APIProviders.AIAvatars.SupportedFormats") ||
                          "Supported: MP4, MOV, AVI"}
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Image to Video - Upload Image */}
            {this.state.aideaSourceType === "image-to-video" && this.state.imageToVideoProvider && (
              <div className="avatar-grid-container">
                <div className="upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={this.handleImageUpload}
                    style={{ display: "none" }}
                    id="image-upload-input"
                  />
                  {this.state.uploadingImage ? (
                    <div className="upload-box uploading">
                      <i className="material-icons upload-icon rotating">hourglass_empty</i>
                      <div className="upload-text">
                        {i18n.t("FileModal.APIProviders.AIAvatars.UploadingImage") ||
                          "Uploading image..."}
                      </div>
                    </div>
                  ) : this.state.uploadedImageUrl ? (
                    <div className="preview-wrapper">
                      <img
                        src={this.state.uploadedImageUrl}
                        alt="Preview"
                        className="uploaded-preview"
                      />
                      <button
                        className="change-file-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("image-upload-input").click();
                        }}
                      >
                        <i className="material-icons">refresh</i>
                        {i18n.t("FileModal.APIProviders.AIAvatars.ChangeImage") || "Change Image"}
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="image-upload-input" className="upload-box">
                      <i className="material-icons upload-icon">image</i>
                      <div className="upload-text">
                        {i18n.t("FileModal.APIProviders.AIAvatars.ClickToUploadImage") ||
                          "Click to upload image"}
                      </div>
                      <div className="upload-hint">
                        {i18n.t("FileModal.APIProviders.AIAvatars.SupportedImageFormats") ||
                          "Supported: JPG, PNG"}
                      </div>
                    </label>
                  )}
                </div>

                {/* Image Guidelines Box */}
                <div className="image-guidelines-box">
                  <h4 className="guidelines-header">
                    <i className="material-icons">info</i>
                    <span>
                      {i18n.t("FileModal.APIProviders.AIAvatars.ImageGuidelinesTitle") ||
                        "Example Guidelines"}
                    </span>
                  </h4>
                  <div className="guidelines-content">
                    {/* Example Photo */}
                    <div className="example-photo-section">
                      <p className="section-label">
                        {i18n.t("FileModal.APIProviders.AIAvatars.ExamplePhoto") || "Example Photo"}
                      </p>
                      <img
                        src="https://aideastorage.blob.core.windows.net/middlewareblobdev/organizations/913a3b21-749c-4295-a71f-0e18a0b3a4d0/user/5c5cec7e-e682-4bc4-8a0f-8342c24737aa/image-to-avatar/input-931e1dcd-close-up-portrait-curly-handsome-european-male_176532-8133.png"
                        alt="Example Avatar"
                        className="example-image"
                      />
                    </div>

                    {/* Guidelines */}
                    <div className="guidelines-list-section">
                      <p className="section-label">
                        {i18n.t("FileModal.APIProviders.AIAvatars.GuidelinesTitle") ||
                          "Photo Requirements"}
                      </p>
                      <ul className="guidelines-list">
                        <li>
                          <i className="material-icons check-icon">check_circle</i>
                          <span>
                            {i18n.t("FileModal.APIProviders.AIAvatars.Guideline1") ||
                              "Face is front-facing and clear"}
                          </span>
                        </li>
                        <li>
                          <i className="material-icons check-icon">check_circle</i>
                          <span>
                            {i18n.t("FileModal.APIProviders.AIAvatars.Guideline2") ||
                              "Plain background without distractions"}
                          </span>
                        </li>
                        <li>
                          <i className="material-icons check-icon">check_circle</i>
                          <span>
                            {i18n.t("FileModal.APIProviders.AIAvatars.Guideline3") ||
                              "Well-lit with good lighting"}
                          </span>
                        </li>
                        <li>
                          <i className="material-icons check-icon">check_circle</i>
                          <span>
                            {i18n.t("FileModal.APIProviders.AIAvatars.Guideline4") ||
                              "Close-up facial shot"}
                          </span>
                        </li>
                        <li>
                          <i className="material-icons check-icon">check_circle</i>
                          <span>
                            {i18n.t("FileModal.APIProviders.AIAvatars.Guideline5") ||
                              "Eyes open and looking at camera"}
                          </span>
                        </li>
                        <li>
                          <i className="material-icons check-icon">check_circle</i>
                          <span>
                            {i18n.t("FileModal.APIProviders.AIAvatars.Guideline6") ||
                              "Natural expression and pose"}
                          </span>
                        </li>
                        <li>
                          <i className="material-icons check-icon">check_circle</i>
                          <span>
                            {i18n.t("FileModal.APIProviders.AIAvatars.Guideline7") ||
                              "High-resolution photo"}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="step-actions">
            <div className="spacer"></div>
            <button
              className="step-btn next-btn"
              disabled={
                !this.state.aideaSourceType ||
                (this.state.aideaSourceType === "stock-avatar" &&
                  !this.state.selectedAideaAvatar) ||
                (this.state.aideaSourceType === "upload-video" && !this.state.uploadedVideo) ||
                (this.state.aideaSourceType === "image-to-video" &&
                  (!this.state.imageToVideoProvider || !this.state.uploadedImage))
              }
              onClick={this.handleNextStep}
            >
              <span>{i18n.t("FileModal.APIProviders.AIAvatars.Next") || "Next"}</span>
              <i className="material-icons">arrow_forward</i>
            </button>
          </div>
        </div>
      );
    }

    // Original Azure/Tavus flow
    return (
      <div className="step-content step-1">
        <div className="avatar-selection">
          <div className="filter-section">
            <div className="form-row inline">
              <div className="form-group">
                <h5 id="field-label">
                  {i18n.t("FileModal.APIProviders.AIAvatars.ProviderType") || "Provider Type"}
                </h5>
                <select
                  value={this.state.selectedProvider}
                  onChange={this.handleProviderChange}
                  className="select"
                >
                  {PROVIDERS.map((provider) => (
                    <option key={provider.value} value={provider.value}>
                      {provider.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <h5 id="field-label">
                  {i18n.t("FileModal.APIProviders.AIAvatars.Gender") || "Gender"}
                </h5>
                <select
                  value={this.state.genderFilter}
                  onChange={this.handleGenderFilterChange}
                  className="select"
                >
                  {GENDER_FILTERS.map((filter) => (
                    <option key={filter.value} value={filter.value}>
                      {i18n.t(filter.labelKey) || filter.defaultLabel}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="avatar-grid-container">
            <div className="avatar-grid">
              {this.state.filteredAvatars.length === 0 && this.state.msg ? (
                <div className="loading-state">
                  <i className="material-icons icon">
                    {this.state.msg.includes("Loading") ? "hourglass_empty" : "error_outline"}
                  </i>
                  <div className="message">{this.state.msg}</div>
                </div>
              ) : this.state.filteredAvatars.length === 0 ? (
                <div className="loading-state">
                  <i className="material-icons icon">person_off</i>
                  <div className="message">
                    {i18n.t("FileModal.APIProviders.AIAvatars.NoAvatarsMatch") ||
                      "No avatars match the selected filters"}
                  </div>
                </div>
              ) : (
                this.state.filteredAvatars.map((avatar) => (
                  <div
                    key={avatar.id}
                    className={`avatar-card ${this.state.selectedAvatar?.id === avatar.id ? "selected" : ""}`}
                    onClick={() => this.selectAvatar(avatar)}
                  >
                    <div className="card-body">
                      <div className="image-container">
                        {avatar.thumbnailUrl ? (
                          <img
                            src={avatar.thumbnailUrl}
                            alt={avatar.name}
                            className="thumbnail"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className={`placeholder-icon ${avatar.thumbnailUrl ? "" : "visible"}`}
                          style={{ display: avatar.thumbnailUrl ? "none" : "flex" }}
                        >
                          <i className="material-icons icon">smart_toy</i>
                        </div>
                        {avatar.videoUrl && (
                          <div className="duration-badge">
                            {avatar.duration ? `${avatar.duration}s` : "Preview"}
                          </div>
                        )}
                        {avatar.category && <div className="category-badge">{avatar.category}</div>}
                      </div>
                      <div className="info">
                        <div className="name" title={avatar.name}>
                          {avatar.name}
                        </div>
                        {avatar.gender && (
                          <div
                            className="details"
                            title={`${this.getGenderLabel(avatar.gender)} • ${this.getProviderLabel(
                              avatar.providerType
                            )}`}
                          >
                            {this.getGenderLabel(avatar.gender)} •{" "}
                            {this.getProviderLabel(avatar.providerType)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="step-actions">
          <div className="spacer"></div>
          <button
            className="step-btn next-btn"
            disabled={!this.state.selectedAvatar}
            onClick={this.handleNextStep}
          >
            <span>{i18n.t("FileModal.APIProviders.AIAvatars.Next") || "Next"}</span>
            <i className="material-icons">arrow_forward</i>
          </button>
        </div>
      </div>
    );
  }

  renderStep2() {
    const { selectedAvatar, selectedProvider } = this.state;

    // Aidea Step 2 - similar layout to Azure/Tavus
    if (selectedProvider === "aidea") {
      return (
        <div className="step-content step-2">
          <div className="step-2-layout">
            {/* Left side: Preview */}
            <div className="preview-panel">
              <h6 className="panel-title">
                {i18n.t("FileModal.APIProviders.AIAvatars.SelectedSource") || "Selected Source"}
              </h6>
              <div className="preview-media">
                {this.state.aideaSourceType === "stock-avatar" &&
                this.state.selectedAideaAvatar?.videoUrl ? (
                  <video
                    key={this.state.selectedAideaAvatar.id}
                    src={this.state.selectedAideaAvatar.videoUrl}
                    controls
                    preload="metadata"
                    className="avatar-video-preview"
                    poster={this.state.selectedAideaAvatar.thumbnailUrl}
                  >
                    {i18n.t("FileModal.APIProviders.AIAvatars.VideoNotSupported") ||
                      "Your browser does not support video playback."}
                  </video>
                ) : this.state.aideaSourceType === "stock-avatar" &&
                  this.state.selectedAideaAvatar?.thumbnailUrl ? (
                  <div className="avatar-image-preview">
                    <img
                      src={this.state.selectedAideaAvatar.thumbnailUrl}
                      alt={this.state.selectedAideaAvatar.name}
                      onError={(e) => {
                        e.target.src = placeholder;
                      }}
                    />
                  </div>
                ) : this.state.aideaSourceType === "upload-video" && this.state.uploadedVideoUrl ? (
                  <video
                    src={this.state.uploadedVideoUrl}
                    controls
                    preload="metadata"
                    className="avatar-video-preview"
                  >
                    {i18n.t("FileModal.APIProviders.AIAvatars.VideoNotSupported") ||
                      "Your browser does not support video playback."}
                  </video>
                ) : this.state.aideaSourceType === "image-to-video" &&
                  this.state.uploadedImageUrl ? (
                  <div className="avatar-image-preview">
                    <img
                      src={this.state.uploadedImageUrl}
                      alt="Uploaded"
                      onError={(e) => {
                        e.target.src = placeholder;
                      }}
                    />
                  </div>
                ) : null}
              </div>
              <div className="avatar-details">
                {this.state.aideaSourceType === "stock-avatar" &&
                  this.state.selectedAideaAvatar && (
                    <>
                      <h5 className="avatar-name">{this.state.selectedAideaAvatar.name}</h5>
                      <p className="avatar-meta">
                        {this.state.selectedAideaAvatar.gender?.charAt(0).toUpperCase() +
                          this.state.selectedAideaAvatar.gender?.slice(1)}{" "}
                        • {this.state.selectedAideaAvatar.category}
                      </p>
                    </>
                  )}
                {this.state.aideaSourceType === "upload-video" && (
                  <h5 className="avatar-name">
                    {i18n.t("FileModal.APIProviders.AIAvatars.UploadedVideo") || "Uploaded Video"}
                  </h5>
                )}
                {this.state.aideaSourceType === "image-to-video" && (
                  <>
                    <h5 className="avatar-name">
                      {i18n.t("FileModal.APIProviders.AIAvatars.ImageToVideo") || "Image to Video"}
                    </h5>
                    <p className="avatar-meta">
                      {this.state.imageToVideoProvider === "gemini" ? "Gemini" : "Grok"}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Right side: Audio Settings */}
            <div className="settings-panel">
              <div className="form-section">
                <div className="form-group stacked">
                  <h5 className="field-label">
                    {i18n.t("FileModal.APIProviders.AIAvatars.AudioSource") || "Audio Source"}
                  </h5>
                  <select
                    value={this.state.audioSourceType || ""}
                    onChange={(e) => this.handleAudioSourceTypeChange(e.target.value)}
                    className="select"
                  >
                    <option value="">
                      {i18n.t("FileModal.APIProviders.AIAvatars.SelectAudioSource") ||
                        "Select Audio Source"}
                    </option>
                    {AUDIO_SOURCE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {i18n.t(type.labelKey) || type.defaultLabel}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Upload Audio */}
                {this.state.audioSourceType === "upload-audio" && (
                  <>
                    <div className="form-group stacked">
                      <div className="audio-upload-area">
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={this.handleAudioUpload}
                          style={{ display: "none" }}
                          id="audio-upload-input"
                        />
                        {this.state.uploadingAudio ? (
                          <div className="audio-upload-box uploading">
                            <i className="material-icons rotating">hourglass_empty</i>
                            <span>
                              {i18n.t("FileModal.APIProviders.AIAvatars.UploadingAudio") ||
                                "Uploading audio..."}
                            </span>
                          </div>
                        ) : this.state.uploadedAudioUrl ? (
                          <div className="audio-uploaded">
                            <audio
                              src={this.state.uploadedAudioUrl}
                              controls
                              className="audio-control"
                            />
                            <button
                              className="change-file-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("audio-upload-input").click();
                              }}
                            >
                              <i className="material-icons">refresh</i>
                              {i18n.t("FileModal.APIProviders.AIAvatars.ChangeAudio") || "Change"}
                            </button>
                          </div>
                        ) : (
                          <label htmlFor="audio-upload-input" className="audio-upload-box">
                            <i className="material-icons">audiotrack</i>
                            <span>
                              {i18n.t("FileModal.APIProviders.AIAvatars.ClickToUploadAudio") ||
                                "Click to upload audio file"}
                            </span>
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Transparent Background */}
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={this.state.transparentBackground}
                          onChange={(e) =>
                            this.setState({ transparentBackground: e.target.checked })
                          }
                        />
                        <span>
                          {i18n.t("FileModal.APIProviders.AIAvatars.TransparentBackground") ||
                            "Transparent Background"}
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {/* Text to Speech */}
                {this.state.audioSourceType === "text-to-speech" && (
                  <>
                    {/* TTS Provider Selection */}
                    <div className="form-group stacked">
                      <h5 className="field-label">
                        {i18n.t("FileModal.APIProviders.AIAvatars.TTSProvider") || "TTS Provider"}
                      </h5>
                      <select
                        value={this.state.ttsProvider || ""}
                        onChange={(e) => {
                          const provider = e.target.value;
                          this.setState({ ttsProvider: provider });
                          if (provider) {
                            this.loadTTSVoices(provider);
                          }
                        }}
                        className="select"
                      >
                        <option value="">
                          {i18n.t("FileModal.APIProviders.AIAvatars.SelectTTSProvider") ||
                            "Select TTS Provider"}
                        </option>
                        {TTS_PROVIDERS.map((provider) => (
                          <option key={provider.value} value={provider.value}>
                            {provider.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Loading State */}
                    {this.state.isLoadingTTSVoices && (
                      <div className="loading-voices">
                        <i className="material-icons rotating">refresh</i>
                        <span>
                          {i18n.t("FileModal.APIProviders.AIAvatars.LoadingVoices") ||
                            "Loading voices..."}
                        </span>
                      </div>
                    )}

                    {/* Gender Filter - only for upload/image modes */}
                    {this.state.ttsProvider &&
                      this.state.ttsVoices.length > 0 &&
                      (this.state.aideaSourceType === "upload-video" ||
                        this.state.aideaSourceType === "image-to-video") && (
                        <div className="form-group stacked">
                          <h5 className="field-label">
                            {i18n.t("FileModal.APIProviders.AIAvatars.VoiceGender") ||
                              "Voice Gender"}
                          </h5>
                          <select
                            value={this.state.aideaGenderFilter || "all"}
                            onChange={(e) =>
                              this.setState({
                                aideaGenderFilter: e.target.value,
                                ttsSelectedVoice: null,
                              })
                            }
                            className="select"
                          >
                            <option value="all">
                              {i18n.t("FileModal.APIProviders.AIAvatars.AllGenders") ||
                                "All Genders"}
                            </option>
                            <option value="male">
                              {i18n.t("FileModal.APIProviders.AIAvatars.Male") || "Male"}
                            </option>
                            <option value="female">
                              {i18n.t("FileModal.APIProviders.AIAvatars.Female") || "Female"}
                            </option>
                          </select>
                        </div>
                      )}

                    {/* Voice Selection */}
                    {this.state.ttsProvider && this.state.ttsVoices.length > 0 && (
                      <div className="form-group stacked">
                        <h5 className="field-label">
                          {i18n.t("FileModal.APIProviders.AIAvatars.SelectVoice") || "Select Voice"}
                        </h5>
                        <div className="voice-grid">
                          {this.getFilteredTTSVoices().map((voice) => (
                            <div
                              key={voice.id}
                              className={`voice-card ${this.state.ttsSelectedVoice?.id === voice.id ? "selected" : ""}`}
                              onClick={() => this.setState({ ttsSelectedVoice: voice })}
                            >
                              <div className="voice-header">
                                <h6 className="voice-name">{voice.name}</h6>
                                {voice.sampleUrl && (
                                  <button
                                    className="play-sample-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const audio = new Audio(voice.sampleUrl);
                                      audio.play();
                                    }}
                                  >
                                    <i className="material-icons">play_arrow</i>
                                  </button>
                                )}
                              </div>
                              <p className="voice-meta">
                                {voice.language} • {voice.gender}
                              </p>
                              {voice.description && (
                                <p className="voice-description">{voice.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Text Input */}
                    {this.state.ttsSelectedVoice && (
                      <>
                        <div className="form-group stacked">
                          <h5 className="field-label">
                            {i18n.t("FileModal.APIProviders.AIAvatars.ScriptText") || "Script Text"}
                          </h5>
                          <textarea
                            id="aidea_prompttext"
                            maxLength={4096}
                            rows="5"
                            className="textarea"
                            value={this.state.ttsText}
                            onChange={(e) => this.setState({ ttsText: e.target.value })}
                            placeholder={
                              i18n.t("FileModal.APIProviders.AIAvatars.ScriptPlaceholder") ||
                              "Enter text for TTS"
                            }
                          ></textarea>
                          <small className="help-text">
                            {i18n.t("FileModal.APIProviders.AIAvatars.ScriptHint") ||
                              "Maximum 4096 characters"}
                          </small>
                        </div>

                        {/* Transparent Background */}
                        <div className="form-group checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={this.state.transparentBackground}
                              onChange={(e) =>
                                this.setState({ transparentBackground: e.target.checked })
                              }
                            />
                            <span>
                              {i18n.t("FileModal.APIProviders.AIAvatars.TransparentBackground") ||
                                "Transparent Background"}
                            </span>
                          </label>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Result section */}
          <div
            id="avatar_video_file_row"
            className={`result-section ${this.state.avatarLoaded ? "" : "hidden"}`}
          >
            <video id="avatar_video" controls src={this.state.videoUrl} className="video" />
            <div className="success-message">
              <h6 className="title">
                <i className="material-icons icon">check_circle</i>
                {i18n.t("FileModal.APIProviders.AIAvatars.VideoGenerated")}
              </h6>
              <small className="description">
                {i18n.t("FileModal.APIProviders.AIAvatars.AccessPrevious")}
              </small>
            </div>
          </div>

          <div className="step-actions">
            <button className="step-btn back-btn" onClick={this.handlePrevStep}>
              <i className="material-icons">arrow_back</i>
              <span>{i18n.t("FileModal.APIProviders.AIAvatars.Back") || "Back"}</span>
            </button>
            <button
              id="avatar_createfile"
              className="step-btn create-btn"
              disabled={
                !this.state.audioSourceType ||
                (this.state.audioSourceType === "upload-audio" && !this.state.uploadedAudio) ||
                this.state.processing ||
                this.state.polling
              }
              onClick={(e) => {
                this.avatarGenerateHandler(e);
              }}
            >
              {this.state.polling && (
                <div className="spinner" role="status" aria-hidden="true"></div>
              )}
              <span>
                {this.state.polling
                  ? i18n.t("FileModal.APIProviders.AIAvatars.Processing")
                  : i18n.t("Create")}
              </span>
            </button>
          </div>

          {(this.state.processing || this.state.polling) && (
            <div id="avatar_status" className="status-message">
              {this.state.jobStatus
                ? i18n.t("FileModal.APIProviders.AIAvatars.JobStatus")
                : i18n.t("FileModal.APIProviders.AIAvatars.PleaseWait")}
            </div>
          )}
        </div>
      );
    }

    // Original Azure/Tavus flow
    return (
      <div className="step-content step-2">
        <div className="step-2-layout">
          {/* Left side: Preview */}
          <div className="preview-panel">
            <h6 className="panel-title">
              {i18n.t("FileModal.APIProviders.AIAvatars.SelectedAvatar")}
            </h6>
            <div className="preview-media">
              {selectedAvatar?.videoUrl ? (
                <video
                  key={selectedAvatar.id}
                  src={selectedAvatar.videoUrl}
                  controls
                  preload="metadata"
                  className="avatar-video-preview"
                  poster={selectedAvatar.thumbnailUrl}
                >
                  {i18n.t("FileModal.APIProviders.AIAvatars.VideoNotSupported") ||
                    "Your browser does not support video playback."}
                </video>
              ) : (
                <div className="avatar-image-preview">
                  <img
                    src={selectedAvatar?.thumbnailUrl}
                    alt={selectedAvatar?.name}
                    onError={(e) => {
                      e.target.src = placeholder;
                    }}
                  />
                </div>
              )}
            </div>
            <div className="avatar-details">
              <h5 className="avatar-name">{selectedAvatar?.name}</h5>
              <p className="avatar-meta">
                {this.getGenderLabel(selectedAvatar?.gender)} • {selectedAvatar?.category}
              </p>
            </div>
          </div>

          {/* Right side: Form */}
          <div className="settings-panel">
            <div className="form-section">
              <div className="form-group stacked">
                <h5 className="field-label">
                  {i18n.t("FileModal.APIProviders.AIAvatars.ScriptText")}
                </h5>
                <textarea
                  id="avatar_prompttext"
                  maxLength={4096}
                  rows="5"
                  className="textarea"
                  placeholder={i18n.t("FileModal.APIProviders.AIAvatars.ScriptPlaceholder")}
                ></textarea>
                <small className="help-text">
                  {i18n.t("FileModal.APIProviders.AIAvatars.ScriptHint")}
                </small>
              </div>

              {(selectedProvider === "azure-video" || selectedProvider === "azure-photo") && (
                <>
                  <div className="form-row">
                    <div className="label-col">
                      <h5 id="field-label">{i18n.t("AiLanguage")}</h5>
                    </div>
                    <div className="input-col">
                      <select
                        id="avatar_lang"
                        className="select"
                        value={this.state.selectedLanguage}
                        onChange={(e) => this.setState({ selectedLanguage: e.target.value })}
                      >
                        <option value="tr-TR">Turkish (tr-TR)</option>
                        <option value="en-US">English (en-US)</option>
                        <option value="az-AZ">Azerbaijani (az-AZ)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="label-col">
                      <h5 id="field-label">{i18n.t("AiSound")}</h5>
                    </div>
                    <div className="input-col">
                      {this.state.isLoadingVoices ? (
                        <div className="select loading-voices">
                          {i18n.t("FileModal.APIProviders.AIAvatars.LoadingVoices") ||
                            "Loading voices..."}
                        </div>
                      ) : (
                        <div className="voice-select-row">
                          <select
                            id="avatar_voice"
                            className="select"
                            value={this.state.selectedVoice}
                            onChange={(e) => this.setState({ selectedVoice: e.target.value })}
                          >
                            {this.state.voices.length === 0 && (
                              <option value="">
                                {i18n.t("FileModal.APIProviders.AIAvatars.NoVoicesAvailable") ||
                                  "No voices available"}
                              </option>
                            )}
                            {this.state.voices.map((voice) => (
                              <option key={voice.id} value={voice.id}>
                                {voice.name}
                              </option>
                            ))}
                          </select>
                          {this.state.selectedVoice &&
                            this.state.voices.find((v) => v.id === this.state.selectedVoice)
                              ?.sampleUrl && (
                              <button
                                type="button"
                                className="voice-sample-btn"
                                onClick={() => {
                                  const voice = this.state.voices.find(
                                    (v) => v.id === this.state.selectedVoice
                                  );
                                  if (voice?.sampleUrl) {
                                    const audio = new Audio(voice.sampleUrl);
                                    audio.play();
                                  }
                                }}
                                title={
                                  i18n.t("FileModal.APIProviders.AIAvatars.PlayVoiceSample") ||
                                  "Play voice sample"
                                }
                              >
                                <i className="material-icons">play_arrow</i>
                              </button>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {selectedProvider === "tavus" && (
                <div className="form-row">
                  <h5 id="field-label switch-label">
                    {i18n.t("FileModal.APIProviders.AIAvatars.TransparentBackground") ||
                      "Transparent Background"}
                  </h5>
                  <div className="input-col">
                    <div className="switch-container">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={this.state.transparentBackground}
                          onChange={(e) =>
                            this.setState({ transparentBackground: e.target.checked })
                          }
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Result section */}
        <div
          id="avatar_video_file_row"
          className={`result-section ${this.state.avatarLoaded ? "" : "hidden"}`}
        >
          <video id="avatar_video" controls src={this.state.videoUrl} className="video" />
          <div className="success-message">
            <h6 className="title">
              <i className="material-icons icon">check_circle</i>
              {i18n.t("FileModal.APIProviders.AIAvatars.VideoGenerated")}
            </h6>
            <small className="description">
              {i18n.t("FileModal.APIProviders.AIAvatars.AccessPrevious")}
            </small>
          </div>
        </div>

        <div className="step-actions">
          <button className="step-btn back-btn" onClick={this.handlePrevStep}>
            <i className="material-icons">arrow_back</i>
            <span>{i18n.t("FileModal.APIProviders.AIAvatars.Back") || "Back"}</span>
          </button>
          <button
            id="avatar_createfile"
            className="step-btn create-btn"
            disabled={
              ((selectedProvider === "azure-video" || selectedProvider === "azure-photo") &&
                this.state.voices.length === 0) ||
              this.state.processing ||
              this.state.polling
            }
            onClick={(e) => {
              this.avatarGenerateHandler(e);
            }}
          >
            {this.state.polling && <div className="spinner" role="status" aria-hidden="true"></div>}
            <span>
              {this.state.polling
                ? i18n.t("FileModal.APIProviders.AIAvatars.Processing")
                : i18n.t("Create")}
            </span>
          </button>
        </div>

        {(this.state.processing || this.state.polling) && (
          <div id="avatar_status" className="status-message">
            {this.state.jobStatus
              ? i18n.t("FileModal.APIProviders.AIAvatars.JobStatus")
              : i18n.t("FileModal.APIProviders.AIAvatars.PleaseWait")}
          </div>
        )}
      </div>
    );
  }

  render() {
    return (
      <div className="contentComponent avatar-container">
        <div id="avatar-maingrid">
          {this.renderStepIndicator()}

          <div className="stepper-content">
            {this.state.currentStep === 1 && this.renderStep1()}
            {this.state.currentStep === 2 && this.renderStep2()}
          </div>
        </div>
      </div>
    );
  }

  async avatarGenerateHandler(e) {
    const { selectedProvider, aideaSourceType, audioSourceType } = this.state;

    // Aidea validation and generation
    if (selectedProvider === "aidea") {
      // Validate source type selection
      if (!aideaSourceType) {
        this.props.onServerModal(
          i18n.t("error.warning"),
          "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoSourceType") + "</span>",
          false
        );
        return;
      }

      // Validate based on source type
      if (aideaSourceType === "stock-avatar" && !this.state.selectedAideaAvatar) {
        this.props.onServerModal(
          i18n.t("error.warning"),
          "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoAvatar") + "</span>",
          false
        );
        return;
      }

      if (aideaSourceType === "upload-video" && !this.state.uploadedVideo) {
        this.props.onServerModal(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoVideo") || "Please upload a video") +
            "</span>",
          false
        );
        return;
      }

      if (aideaSourceType === "image-to-video") {
        if (!this.state.imageToVideoProvider) {
          this.props.onServerModal(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoProvider") ||
                "Please select a provider") +
              "</span>",
            false
          );
          return;
        }
        if (!this.state.uploadedImage) {
          this.props.onServerModal(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoImage") ||
                "Please upload an image") +
              "</span>",
            false
          );
          return;
        }
      }

      // Validate audio source
      if (!audioSourceType) {
        this.props.onServerModal(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoAudioSource") ||
              "Please select audio source") +
            "</span>",
          false
        );
        return;
      }

      if (audioSourceType === "upload-audio" && !this.state.uploadedAudio) {
        this.props.onServerModal(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoAudio") || "Please upload audio") +
            "</span>",
          false
        );
        return;
      }

      if (audioSourceType === "text-to-speech") {
        // Validate TTS provider
        if (!this.state.ttsProvider) {
          this.props.onServerModal(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoTTSProvider") ||
                "Please select TTS provider") +
              "</span>",
            false
          );
          return;
        }

        // Validate voice selection
        if (!this.state.ttsSelectedVoice) {
          this.props.onServerModal(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoVoice") || "Please select a voice") +
              "</span>",
            false
          );
          return;
        }

        // Validate text
        if (!this.state.ttsText || this.state.ttsText.trim() === "") {
          this.props.onServerModal(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoText") || "Please enter text") +
              "</span>",
            false
          );
          return;
        }
      }

      // Start Aidea generation
      this.setState({ processing: true, videoUrl: "", avatarLoaded: false });

      try {
        const searchParams = new URLSearchParams(window.location.search);
        const trainingId = searchParams?.get("id");
        const navId = this.props.navItemSelected;

        console.log("[AvatarTextToSpeech] navId:", navId, "trainingId:", trainingId);

        // Special handling for image-to-video
        if (aideaSourceType === "image-to-video") {
          // Build query parameters for image-to-avatar endpoint
          const queryParams = new URLSearchParams();
          if (trainingId) {
            queryParams.set("trainingId", trainingId);
          }
          if (navId) {
            queryParams.set("navId", navId);
          }

          console.log("[AvatarTextToSpeech] Image-to-video queryParams:", queryParams.toString());

          // Add TTS parameters to query string if text-to-speech is selected
          if (audioSourceType === "text-to-speech") {
            queryParams.set("text", this.state.ttsText.trim());
            queryParams.set("ttsProvider", this.state.ttsProvider);
            queryParams.set("voiceId", this.state.ttsSelectedVoice.id);
            queryParams.set("voiceLanguage", this.state.ttsSelectedVoice.language);

            // Add ElevenLabs specific parameters if provider is elevenlabs
            if (this.state.ttsProvider === "elevenlabs") {
              queryParams.set("stability", "0.5");
              queryParams.set("similarity", "0.75");
              queryParams.set("styleExaggeration", "0.0");
              queryParams.set("speakerBoost", "true");
            }
          }

          // Build FormData for image-to-avatar
          const formData = new FormData();

          // Add image file
          if (this.state.uploadedImage) {
            formData.append("file", this.state.uploadedImage);
          }

          // Add audio file if upload-audio is selected
          if (audioSourceType === "upload-audio") {
            if (this.state.uploadedAudio) {
              formData.append("audio", this.state.uploadedAudio);
              const uploadedAudioDuration = await getMediaDuration(this.state.uploadedAudio);
              if (uploadedAudioDuration != null) {
                queryParams.set("durationSeconds", String(uploadedAudioDuration));
              }
            }
          }

          // Add provider and other parameters
          formData.append("provider", this.state.imageToVideoProvider);
          formData.append("applyLipsync", "true");
          formData.append("removeBackground", this.state.transparentBackground ? "true" : "false");

          const response = await fetch(
            `${process.env.MW_API_URL}api/tts-avatar/image-to-avatar/generate?${queryParams.toString()}`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
              body: formData,
            }
          );

          if (response.ok) {
            const result = await response.json();
            const jobId = result.jobId;

            if (jobId) {
              this.setState({
                jobId: jobId,
                processing: false,
                jobStatus: "submitted",
              });
              const promptForNaming =
                audioSourceType === "text-to-speech" ? this.state.ttsText : "Aidea-Image-Avatar";
              this.startJobPolling(jobId, promptForNaming);
            } else {
              throw new Error("No job ID received");
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.message ||
                errorData.error?.message ||
                "Failed to submit image-to-avatar job"
            );
          }

          e.stopPropagation();
          return;
        }

        // Build query parameters for other types (stock-avatar, upload-video)
        const queryParams = new URLSearchParams();

        if (trainingId) {
          queryParams.set("trainingId", trainingId);
        }
        if (navId) {
          queryParams.set("navId", navId);
        }

        queryParams.set("removeBackground", this.state.transparentBackground ? "true" : "false");

        console.log("[AvatarTextToSpeech] ProcessLipsync queryParams:", queryParams.toString());

        // Build FormData
        const formData = new FormData();

        // Add video source based on type
        if (aideaSourceType === "stock-avatar") {
          // For stock avatar, fetch video file and add to FormData
          queryParams.set("avatarImageUrl", this.state.selectedAideaAvatar.thumbnailUrl);
          if (this.state.selectedAideaAvatar.videoUrl) {
            // Fetch video file from URL and add as binary to FormData
            const videoResponse = await fetch(
              this.state.selectedAideaAvatar.videoUrl,
              credentialsFor(this.state.selectedAideaAvatar.videoUrl)
            );
            const videoBlob = await videoResponse.blob();
            formData.append("video", videoBlob, "avatar-video.mov");
          }
        } else if (aideaSourceType === "upload-video") {
          // For uploaded video, send the file
          if (this.state.uploadedVideo) {
            formData.append("video", this.state.uploadedVideo);
          }
          // Also add avatarImageUrl for thumbnail if available
          if (this.state.selectedAideaAvatar?.thumbnailUrl) {
            queryParams.set("avatarImageUrl", this.state.selectedAideaAvatar.thumbnailUrl);
          }
        }

        // Add audio or TTS parameters
        if (audioSourceType === "upload-audio") {
          // Mode 1: Direct audio upload
          if (this.state.uploadedAudio) {
            formData.append("audio", this.state.uploadedAudio);
            const uploadedAudioDuration = await getMediaDuration(this.state.uploadedAudio);
            if (uploadedAudioDuration != null) {
              queryParams.set("durationSeconds", String(uploadedAudioDuration));
            }
          }
        } else if (audioSourceType === "text-to-speech") {
          // Mode 2: TTS parameters
          queryParams.set("text", this.state.ttsText.trim());
          queryParams.set("ttsProvider", this.state.ttsProvider);
          queryParams.set("voiceId", this.state.ttsSelectedVoice.id);
          queryParams.set("voiceLanguage", this.state.ttsSelectedVoice.language);

          // Add ElevenLabs specific parameters if provider is elevenlabs
          if (this.state.ttsProvider === "elevenlabs") {
            queryParams.set("stability", "0.5");
            queryParams.set("similarity", "0.75");
            queryParams.set("styleExaggeration", "0.0");
            queryParams.set("speakerBoost", "true");
          }
        }

        const response = await fetch(
          `${process.env.MW_API_URL}api/tts-avatar/processLipsync/v2?${queryParams.toString()}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
            body: formData,
          }
        );

        if (response.ok) {
          const result = await response.json();
          const jobId = result.jobId;

          if (jobId) {
            this.setState({
              jobId: jobId,
              processing: false,
              jobStatus: "submitted",
            });
            const promptForNaming =
              audioSourceType === "text-to-speech" ? this.state.ttsText : "Aidea-Avatar";
            const lipsyncStatusUrl = `${process.env.MW_API_URL}api/tts-avatar/processLipsync/v2/${jobId}`;
            this.startJobPolling(jobId, promptForNaming, lipsyncStatusUrl);
          } else {
            throw new Error("No job ID received");
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || errorData.error?.message || "Failed to submit lipsync job"
          );
        }
      } catch (error) {
        console.error("Error generating Aidea avatar:", error);
        this.props.onServerModal(
          i18n.t("FileModal.APIProviders.error"),
          "<span>" + error.message + "</span>",
          false
        );
        this.setState({ processing: false });
      }

      e.stopPropagation();
      return;
    }

    // Original Azure/Tavus flow
    let _prompt = document.getElementById("avatar_prompttext").value;

    if (_prompt.replaceAll(" ", "") === "") {
      this.props.onServerModal(
        i18n.t("error.warning"),
        "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoText") + "</span>",
        false
      );
      return;
    }

    if (!this.state.selectedAvatar) {
      this.props.onServerModal(
        i18n.t("error.warning"),
        "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoAvatar") + "</span>",
        false
      );
      return;
    }

    if (
      (this.state.selectedProvider === "azure-video" ||
        this.state.selectedProvider === "azure-photo") &&
      !this.state.selectedVoice
    ) {
      this.props.onServerModal(
        i18n.t("error.warning"),
        "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoVoice") + "</span>",
        false
      );
      return;
    }

    this.setState({ processing: true, videoUrl: "", avatarLoaded: false });

    try {
      const searchParams = new URLSearchParams(window.location.search);
      const id = searchParams?.get("id");
      const navId = this.props.navItemSelected;

      console.log("[AvatarTextToSpeech] Azure/Tavus navId:", navId, "trainingId:", id);

      let endpoint = `${process.env.MW_API_URL}api/tts-avatar/generate`;
      let payload = {};

      if (
        this.state.selectedProvider === "azure-video" ||
        this.state.selectedProvider === "azure-photo"
      ) {
        endpoint = `${process.env.MW_API_URL}api/tts-avatar/generate`;
        payload = {
          avatarId: this.state.selectedAvatar.id,
          text: _prompt.trim(),
          transparent: true,
          language: this.state.selectedLanguage,
          voiceName: this.state.selectedVoice,
          gestures: this.state.selectedAvatar.metadata?.supportedGestures || [],
          idealStudioTrainingId: id || "default",
          trainingId: id || "default",
          avatarImageUrl: this.state.selectedAvatar.thumbnailUrl,
          navId: navId,
        };
      } else if (this.state.selectedProvider === "tavus") {
        endpoint = `${process.env.MW_API_URL}api/tts-avatar/tavus/generate`;
        payload = {
          replicaId: this.state.selectedAvatar.azureAvatarId,
          script: _prompt.trim(),
          transparentBackground: this.state.transparentBackground,
          trainingId: id || "default",
          idealStudioTrainingId: id || "default",
          avatarImageUrl: this.state.selectedAvatar.thumbnailUrl,
          navId: navId,
        };
      }

      console.log("[AvatarTextToSpeech] Payload:", JSON.stringify(payload, null, 2));

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        const jobId = result.data?.jobId || result.jobId;

        if (jobId) {
          this.setState({
            jobId: jobId,
            processing: false,
            jobStatus: "submitted",
          });
          this.startJobPolling(jobId, _prompt);
        } else {
          throw new Error("No job ID received");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData?.error?.message ||
          errorData?.message ||
          errorData?.error ||
          (typeof errorData === "string" ? errorData : null) ||
          "Failed to submit avatar generation job";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error generating avatar:", error);
      this.props.onServerModal(
        i18n.t("FileModal.APIProviders.error"),
        "<span>" + error.message + "</span>",
        false
      );
      this.setState({ processing: false });
    }

    e.stopPropagation();
  }

  startJobPolling(jobId, prompt, statusEndpoint) {
    this.setState({ polling: true });
    this.checkJobStatus(jobId, prompt, statusEndpoint);
  }

  stopJobPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.setState({ polling: false });
  }

  async checkJobStatus(jobId, prompt, statusEndpoint) {
    try {
      const url = statusEndpoint || `${process.env.MW_API_URL}api/tts-avatar/jobs/${jobId}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const jobData = await response.json();
        const jobState = jobData.state || jobData.status;
        this.setState({ jobStatus: jobState });

        const completedVideoUrl =
          jobData.result?.videoUrl ||
          jobData.result?.url ||
          jobData.finalVideoUrl ||
          jobData.videoUrl;

        if (jobState === "completed" && completedVideoUrl) {
          this.stopJobPolling();

          const finalVideoUrl = completedVideoUrl;

          let idd = ID_PREFIX_FILE + Date.now();
          let fnamee = "AI-Avatar-" + prompt;
          if (prompt.length > 50) {
            fnamee = "AI-Avatar-" + prompt.substr(0, 50);
          }

          this.setState({
            videoUrl: finalVideoUrl,
            avatarLoaded: true,
            processing: false,
          });

          this.props.onElementSelected(fnamee, finalVideoUrl, "video", idd);
          // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
          approveJob(jobId);
        } else if (jobState === "failed") {
          this.stopJobPolling();
          this.props.onServerModal(
            i18n.t("FileModal.APIProviders.error"),
            "<span>Avatar generation failed</span>",
            false
          );
          this.setState({ processing: false });
        } else {
          this.pollInterval = setTimeout(() => {
            this.checkJobStatus(jobId, prompt, statusEndpoint);
          }, 3000);
        }
      } else {
        throw new Error("Failed to check job status");
      }
    } catch (error) {
      console.error("Error checking job status:", error);
      this.stopJobPolling();
      this.props.onServerModal(
        i18n.t("FileModal.APIProviders.error"),
        "<span>Error checking avatar generation status</span>",
        false
      );
      this.setState({ processing: false });
    }
  }
}

AvatarTextToSpeechComponent.propTypes = {
  elementSelected: PropTypes.any,
  onElementSelected: PropTypes.func.isRequired,
  icon: PropTypes.any,
  name: PropTypes.string,
  onServerModal: PropTypes.func,
  navItemSelected: PropTypes.string,
};
